import { LngLatBounds, LngLatBoundsLike, Map as MapLibre, Popup } from 'maplibre-gl';
import { MapContent, MapContentBase } from '../MapContent';
import { MapContentVectorSource } from '../MapContentVectorSource';
import { TrackStyles } from '../../../../../configuration/model/TrackStyles';
import { StormTrackLayer } from './StormTrackLayer';
import { MapTools } from '../../../../tools/MapTools';
import { StormTrackArrowsLayer } from './StormTrackArrowsLayer';
import { MapContentSource } from '../MapContentSource';
import { StormMarkerShadowLayer } from './StormMarkerShadowLayer';
import { StormMarkerCircleLayer } from './StormMarkerCircleLayer';
import { StormMarkerSymbolLayer } from './StormMarkerSymbolLayer';
import { Feature, FeatureCollection } from 'geojson';
import { StormInfo, StormShift } from '../../../../models/storm.model';
import { environment } from '../../../../../../environments/environment';
import { ConfigurationModel } from '../../../../models/configuration.model';
import { FetchSource, PMTiles, Protocol } from 'pmtiles';
import { StormMapDataService } from '../../../../../pages/insights/components/storm-report/storm-map-data.service';
import { Observation } from '../../../../models/observation';
import { ObservationShadowLayer } from './ObservationShadowLayer';
import { ObservationCircleLayer } from './ObservationCircleLayer';
import { ObservationIconLayer } from './ObservationIconLayer';
import { ObservationLabelLayer } from './ObservationLabelLayer';
import { HighlightedItemSource } from '../../../../models/highlighted-item-update';
import {
  ObservationInfoWindowContentComponent
} from '../../../map-viewer/observation-info-window-content/observation-info-window-content.component';
import { ApplicationRef, ComponentRef, Type, ViewContainerRef } from '@angular/core';
import { PointFeature, PointProperties } from '../../../../models/GeoJson';
import { Subscription } from 'rxjs';
import { StormMarkerLabelLayer } from './StormMarkerLabelLayer';


export class StormMapContent extends MapContentBase implements MapContent {

  private tracksSource: MapContentVectorSource;
  private readonly tracksLayer: StormTrackLayer;
  private readonly tracksArrowLayer: StormTrackArrowsLayer;
  private readonly stormLengthSeconds: number;
  private markersSource: MapContentSource = new MapContentSource('storm-markers-source');
  private markerShadowLayer: StormMarkerShadowLayer;
  private markerCircleLayer: StormMarkerCircleLayer;
  private markerSymbolLayer: StormMarkerSymbolLayer;
  private markerLabelLayer: StormMarkerLabelLayer;
  private bounds: LngLatBoundsLike;

  private observationsSource: MapContentSource = new MapContentSource('storm-observations-source');
  private observationShadowLayer: ObservationShadowLayer;
  private observationCircleLayer: ObservationCircleLayer;
  private observationIconLayer: ObservationIconLayer;
  private observationLabelLayer: ObservationLabelLayer;

  private readonly observationClickListener: any;
  private lastPopup: Popup;
  private lastComponentRef: ComponentRef<any>;

  private markerOnHoverListener: any;
  private markerOffHoverListener: any;

  // animation properties
  private speedFactor = 10; // 1 = normal time, 10 = 10x faster
  private animation; // to store and cancel the animation
  private animationStartTime = 0;
  private progress = 0; // progress = timestamp - startTime
  private resetTime = false; // indicator of whether time reset is needed for the animation
  private shiftLatestIndex = new Map<number, number>();

  private readonly subscriptions: Subscription[] = [];

  constructor(
    configuration: ConfigurationModel,
    trackStyles: TrackStyles,
    private stormShifts: StormShift[],
    private storm: StormInfo,
    private playbackIndex: any,
    observationFeatureCollection: FeatureCollection,
    private observations: Observation[],
    private viewContainerRef: ViewContainerRef,
    private appRef: ApplicationRef,
    private stormMapDataService: StormMapDataService,
    private currentTimestampUpdated: (unixtime: number) => void,
  ) {
    super();
    this.stormLengthSeconds = this.storm.durationInSeconds;

    const tenantId = configuration.id;
    const pmTilesUrl = `${environment.filesBaseUrl}storms/${tenantId}/${this.storm.id}.track.pmtiles`;

    // fallback bounding box - tenant region
    this.bounds = configuration.region.reduce(
      (bounds, latLngModel) => {
        return bounds.extend([latLngModel.lng, latLngModel.lat]);
      },
      new LngLatBounds(configuration.region[0], configuration.region[0])
    );
    // get bounding box from PMTiles
    const source = new FetchSource(pmTilesUrl, new Headers(), 'include');
    const pmTiles = new PMTiles(source);
    pmTiles.getHeader().then(header => {
      this.bounds = [[header.minLon, header.minLat], [header.maxLon, header.maxLat]];
    });

    // set PMTiles source
    this.tracksSource = new MapContentVectorSource(
      'storm-tracks-source',
      `pmtiles://${pmTilesUrl}`,
      pmTiles,
    );

    this.tracksLayer = new StormTrackLayer(this.tracksSource, trackStyles, stormShifts);
    this.tracksArrowLayer = new StormTrackArrowsLayer(this.tracksSource);

    this.markerShadowLayer = new StormMarkerShadowLayer(this.markersSource);
    this.markerCircleLayer = new StormMarkerCircleLayer(this.markersSource);
    this.markerSymbolLayer = new StormMarkerSymbolLayer(this.markersSource);
    this.markerLabelLayer = new StormMarkerLabelLayer(this.markersSource);

    // storm observations
    this.observationsSource.loadFromFeatureCollection(observationFeatureCollection);
    this.observationShadowLayer = new ObservationShadowLayer(this.observationsSource);
    this.observationCircleLayer = new ObservationCircleLayer(this.observationsSource);
    this.observationIconLayer = new ObservationIconLayer(this.observationsSource);
    this.observationLabelLayer = new ObservationLabelLayer(this.observationsSource);

    // storm observations highlighting and clicking
    this.observationClickListener = (e) => {
      if (MapTools.checkPropagation(e)) {
        return;
      }
      const pointFeature = e.features[0] as PointFeature;
      this.stormMapDataService.sendHighlightedObservationUpdate(
        pointFeature.properties.id,
        HighlightedItemSource.MAP,
      );
    };
    this.subscriptions.push(
      this.stormMapDataService.highlightedObservation$.subscribe(update => {
        if (!!update.itemId) {
          this.observationClicked(update.itemId, update.source);
        }
      })
    );

    // shift highlighting
    this.subscriptions.push(
      this.stormMapDataService.highlightedShift$.subscribe(update => {
        this.tracksLayer.highlightByShiftId(this.map, update.itemId);
        this.markerLabelLayer.toggleVisibility(this.map, !!update.itemId);
      })
    );
    this.markerOnHoverListener = e => {
      if (MapTools.checkPropagation(e)) {
        return;
      }
      const pointFeature = e.features[0] as PointFeature;
      this.stormMapDataService.sendHighlightedShiftUpdate(pointFeature.properties.sessionkey, HighlightedItemSource.MAP);
    };
    this.markerOffHoverListener = e => {
      if (MapTools.checkPropagation(e)) {
        return;
      }
      this.stormMapDataService.sendHighlightedShiftUpdate(null, HighlightedItemSource.MAP);
    };
  }

  load(map: MapLibre, protocol: Protocol, zoomToFeatures: boolean) {
    this.map = map;
    this.protocol = protocol;
    this.firstSymbolLayerId = this.getFirstSymbolLayerId();

    this.tracksSource.updateMap(map, protocol);
    this.map.addLayer(this.tracksLayer.toLayerSpecification(), this.firstSymbolLayerId);
    this.map.addLayer(this.tracksArrowLayer.toLayerSpecification(), this.firstSymbolLayerId);

    this.markersSource.updateMap(this.map);
    this.map.addLayer(this.markerLabelLayer.toLayerSpecification());
    this.map.addLayer(this.markerShadowLayer.toLayerSpecification());
    this.map.addLayer(this.markerCircleLayer.toLayerSpecification());
    this.map.addLayer(this.markerSymbolLayer.toLayerSpecification());

    // shift observations
    this.observationsSource.updateMap(this.map);
    this.map.addLayer(this.observationShadowLayer.toLayerSpecification());
    this.map.addLayer(this.observationCircleLayer.toLayerSpecification());
    this.map.addLayer(this.observationIconLayer.toLayerSpecification());
    this.map.addLayer(this.observationLabelLayer.toLayerSpecification());
    MapTools.pointCursorForLayer(this.map, this.observationCircleLayer, true);
    this.map.on('click', this.observationCircleLayer.layerId, this.observationClickListener);
    this.map.on('mouseenter', this.markerCircleLayer.layerId, this.markerOnHoverListener);
    this.map.on('mouseleave', this.markerCircleLayer.layerId, this.markerOffHoverListener);

    if (zoomToFeatures) {
      this.map.fitBounds(this.bounds, { padding: 20 });
    }
  }

  unload() {
    MapTools.pointCursorForLayer(this.map, this.observationCircleLayer, false);
    this.map.off('click', this.observationCircleLayer.layerId, this.observationClickListener);
    this.map.removeLayer(this.observationLabelLayer.layerId);
    this.map.removeLayer(this.observationIconLayer.layerId);
    this.map.removeLayer(this.observationCircleLayer.layerId);
    this.map.removeLayer(this.observationShadowLayer.layerId);
    this.map.removeSource(this.observationsSource.sourceId);

    this.map.removeLayer(this.tracksArrowLayer.layerId);
    this.map.removeLayer(this.tracksLayer.layerId);
    this.map.removeSource(this.tracksSource.sourceId);

    this.map.removeLayer(this.markerLabelLayer.layerId);
    this.map.removeLayer(this.markerSymbolLayer.layerId);
    this.map.removeLayer(this.markerCircleLayer.layerId);
    this.map.removeLayer(this.markerShadowLayer.layerId);
    this.map.removeSource(this.markersSource.sourceId);

    this.map = null;

    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  // params: percentage
  filterPercent(pctFrom: number, pctTo: number) {
    const timeFrom = this.getAbsTime(pctFrom);
    const timeTo = this.getAbsTime(pctTo);
    MapTools.onMap(this.map, () => {
      this.tracksLayer.filterByTime(this.map, timeFrom, timeTo);
    });
  }

  // external trigger to jump to timestamp
  filterTo(timeTo: number) {
    this.progress = (timeTo - this.storm.startUnix) / this.speedFactor * 1000;
    MapTools.onMap(this.map, () => {
      this.stormMapDataService.filterByTime(timeTo);
      this.tracksLayer.filterByTime(this.map, null, timeTo);
      this.tracksArrowLayer.filterByTime(this.map, null, timeTo);
      this.observationShadowLayer.filterByTime(this.map, null, timeTo);
      this.observationCircleLayer.filterByTime(this.map, null, timeTo);
      this.observationIconLayer.filterByTime(this.map, null, timeTo);
      this.observationLabelLayer.filterByTime(this.map, null, timeTo);

      this.markersSource.clearFeatures();
      this.stormShifts
        .filter(shift => shift.start <= timeTo && shift.end >= timeTo)
        .forEach(shift => {
          const shiftPlaybackIndex = this.playbackIndex[shift.id];
          if (shiftPlaybackIndex) {
            const p = this.findLatestPosition(
              shiftPlaybackIndex,
              timeTo
            );
            const r = this.findLatestPositionIndex(
              shiftPlaybackIndex,
              timeTo
            );

            if (p) {
              const [unixtime, lon, lat, heading] = p;
              this.markersSource.addFeature(
                this.createMarkerFeature(
                  [lon, lat],
                  {
                    id: shift.id,
                    unixtime,
                    heading,
                    sessionkey: shift.id,
                    locationsourceid: shift.vehicleId,
                    title: shift.vehicleName,
                  },
                )
              );
            }
            if (r >= 0) {
              this.shiftLatestIndex.set(shift.id, r);
            }
          } else {
           // missing data for shift in the playback index
           console.warn('Missing data in playback index for shift=', shift.id);
          }
        });
      this.markersSource.updateMap(this.map);
    });
  }

  findLatestPosition(points: number[][], timestamp: number): number[] {
    let lo = 0;
    let hi = points.length - 1;

    let best = null;

    while (lo <= hi) {

      const mid = (lo + hi) >> 1;

      if (points[mid][0] <= timestamp) {
        best = points[mid];
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }

    return best;
  }

  findLatestPositionIndex(points: number[][], timestamp: number): number {
    let lo = 0;
    let hi = points.length - 1;

    let best = null;
    let bestIndex = -1;

    while (lo <= hi) {

      const mid = (lo + hi) >> 1;

      if (points[mid][0] <= timestamp) {
        best = points[mid];
        bestIndex = mid;
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }

    return bestIndex;
  }

  createMarkerFeature(coordinates: number[], properties: PointProperties): Feature {
    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates,
      },
      properties,
    };
  }

  private getAbsTime(relativePosition: number): number {
    // unknown start or length
    if (!this.storm.startUnix || !this.stormLengthSeconds) {
      return null;
    }
    // do not filter start or end at all
    if (relativePosition === 0 || relativePosition === 100) {
      return null;
    }
    return Math.round(this.storm.startUnix + relativePosition / 100 * this.stormLengthSeconds);
  }

  changeAnimationSpeed(speedFactor: number) {
    this.speedFactor = speedFactor;
  }

  playAnimation() {
    // eslint-disable-next-line no-console
    console.log('playing animation');
    this.animationStartTime = performance.now();
    this.resetTime = true;
    this.animateLine(this.animationStartTime);
  }

  // DOMHighResTimeStamp parameter
  private animateLine(timestamp) {
    if (this.resetTime) {
      // resume previous progress
      this.animationStartTime = timestamp - this.progress;
      this.resetTime = false;
    } else {
      this.progress = timestamp - this.animationStartTime;
    }

    if (this.progress / 1000 * this.speedFactor > this.stormLengthSeconds) {
      // eslint-disable-next-line no-console
      console.log('Storm end has been reached.');
      this.stormMapDataService.sendStormEndReached();
      return;
    }

    const calcNextPointTime = this.progress / 1000 * this.speedFactor;
    this.markersSource.clearFeatures();
    this.currentTimestampUpdated(this.storm.startUnix + calcNextPointTime);
    this.stormShifts
      .filter(shift => shift.start <= this.storm.startUnix + calcNextPointTime && shift.end >= this.storm.startUnix + calcNextPointTime)
      .forEach(shift => {
        const shiftPlaybackIndex = this.playbackIndex[shift.id];
        if (shiftPlaybackIndex) {
          if (this.shiftLatestIndex.get(shift.id) === undefined || shiftPlaybackIndex[this.shiftLatestIndex.get(shift.id)]) {
            const nextPointTime = shiftPlaybackIndex[this.shiftLatestIndex.get(shift.id) ?? 0][0] - this.storm.startUnix;
            if (nextPointTime < calcNextPointTime) {
              // use the latest point and move index
              this.markersSource.addFeature(
                this.createMarkerFeature(
                  [
                    shiftPlaybackIndex[this.shiftLatestIndex.get(shift.id) ?? 0][1],
                    shiftPlaybackIndex[this.shiftLatestIndex.get(shift.id) ?? 0][2],
                  ],
                  {
                    id: shift.id,
                    unixtime: nextPointTime,
                    heading: shiftPlaybackIndex[this.shiftLatestIndex.get(shift.id) ?? 0][3],
                    sessionkey: shift.id,
                    locationsourceid: shift.vehicleId,
                    title: shift.vehicleName,
                  }
                )
              );
              const timeTo = this.storm.startUnix + nextPointTime;
              this.shiftLatestIndex.set(shift.id, (this.shiftLatestIndex.get(shift.id) ?? 0) + 1);
              this.tracksLayer.filterByTime(this.map, null, timeTo);
              this.tracksArrowLayer.filterByTime(this.map, null, timeTo);
              this.observationShadowLayer.filterByTime(this.map, null, timeTo);
              this.observationCircleLayer.filterByTime(this.map, null, timeTo);
              this.observationIconLayer.filterByTime(this.map, null, timeTo);
              this.observationLabelLayer.filterByTime(this.map, null, timeTo);
            } else {
              // keeping index and interpolate
              const lastIndex = this.shiftLatestIndex.get(shift.id) ?? 0;
              if (lastIndex > 0) {
                const from = shiftPlaybackIndex[lastIndex - 1];
                const fromLng = from[1];
                const fromLat = from[2];
                const fromTime = from[0] - this.storm.startUnix;
                const to = shiftPlaybackIndex[lastIndex];
                const toLng = to[1];
                const toLat = to[2];
                const toTime = to[0] - this.storm.startUnix;
                const timeDiff = toTime - fromTime;
                const timeBetweenPoints = calcNextPointTime - fromTime;
                const pctBetweenPoints = timeBetweenPoints / timeDiff;
                const interpolatedLng = fromLng + (toLng - fromLng) * pctBetweenPoints;
                const interpolatedLat = fromLat + (toLat - fromLat) * pctBetweenPoints;
                // this.vehicleMarkerClicked(
                // this.shiftPointFeatures[this.latestIndex - 1] as PointFeature, calcNextPointTime + this.storm.startUnix
                // );
                this.markersSource.addFeature(
                  this.createMarkerFeature([interpolatedLng, interpolatedLat], {
                    id: shift.id,
                    unixtime: nextPointTime,
                    heading: to[3],
                    sessionkey: shift.id,
                    locationsourceid: shift.vehicleId,
                    title: shift.vehicleName,
                  })
                );
              }
            }
          }
        } else {
          // skipping shift animation as it has no data in playback index
        }
      });
    this.markersSource.updateMap(this.map);


    // Request the next frame of the animation.
    this.animation = requestAnimationFrame(time => this.animateLine(time));
  }

  stopAnimation() {
    // eslint-disable-next-line no-console
    console.log('stopping animation...');
    this.shiftLatestIndex.clear();
    this.progress = 0;
    cancelAnimationFrame(this.animation);
  }

  private observationClicked(observationId: number, source: HighlightedItemSource) {
    this.observationsSource.features.forEach(feature => {
      feature.properties.highlighted = feature.properties.id === observationId;
    });
    if (!!this.map) {
      this.observationsSource.updateMap(this.map);
      const observation = this.observations.find(o => o.id === observationId);
      if (!!observation) {
        this.showObservationInfo(observation);
      }
    } else {
      console.warn('Map not initialized');
    }
  }

  private instantiateComponent<T>(componentType: Type<T>): ComponentRef<T> {
    const componentRef = this.viewContainerRef.createComponent(componentType);
    componentRef.onDestroy(() => {
      this.appRef.detachView(componentRef.hostView);
    });
    this.lastComponentRef = componentRef;

    return componentRef;
  }

  private showObservationInfo(observation: Observation) {
    this.lastPopup?.remove();
    const infoComponentRef = this.instantiateComponent(ObservationInfoWindowContentComponent);
    infoComponentRef.setInput('observation', observation);
    infoComponentRef.setInput('isLiveMap', false);

    const div = document.createElement('div');
    div.appendChild(this.lastComponentRef.location.nativeElement);

    this.lastPopup = new Popup({offset: [0, -15], className: 'marker-popup', maxWidth: '300px'})
      .setLngLat([observation.location.longitude, observation.location.latitude])
      .setDOMContent(div)
      .addTo(this.map);
  }
}
